<template>
  <div class="user-modal">
    <LmDialog v-model="open" :title="modalTitle" :styles="{ minWidth: '900px' }">
      <template #toolbar-actions>
        <q-btn color="tertiary" size="sm" @click.native="handleSave">
          Save
        </q-btn>
      </template>
      <template #content>
        <q-card-section>
          <div class="row q-col-gutter-md wrap items-end">
            <div class="col-xs-12 col-md-4">
              <q-input
                v-model="$v.payload.name.$model"
                :error="$v.payload.name.$error"
                :error-message="$v_displayMessage($v.payload.name)"
                stack-label
                label="Name *"
                outlined
              />
            </div>
            <div class="col-xs-12 col-md-4">
              <q-input
                v-model.trim="$v.payload.email.$model"
                :error="$v.payload.email.$error"
                :error-message="$v_displayMessage($v.payload.email)"
                stack-label
                label="Email *"
                type="email"
                outlined
              />
            </div>
            <div class="col-xs-12 col-md-4">
              <q-input v-model.trim="payload.phone" stack-label label="Phone" type="tel" outlined bottom-slots />
            </div>
            <div class="col-xs-12 col-md-4">
              <q-input v-model.trim="payload.job" stack-label label="Job" outlined bottom-slots />
            </div>
            <div v-if="modalType === 'create'" class="col-xs-12 col-md-4">
              <q-input
                v-model.trim="$v.payload.password.$model"
                :error="$v.payload.password.$error"
                :error-message="$v_displayMessage($v.payload.password)"
                stack-label
                label="Password *"
                type="password"
                class="password-field"
                autocomplete="new-password"
                outlined
              />
            </div>
            <div v-if="modalType === 'create'" class="col-xs-12 col-md-4">
              <q-input
                v-model.trim="$v.payload.password_confirmation.$model"
                :error="$v.payload.password_confirmation.$error"
                :error-message="$v_displayMessage($v.payload.password_confirmation)"
                stack-label
                label="Confirm Password *"
                type="password"
                class="password-field"
                autocomplete="new-password"
                outlined
              />
            </div>
            <div class="col-xs-12 col-md-4">
              <q-select
                v-model="$v.payload.role_id.$model"
                :error="$v.payload.role_id.$error"
                :error-message="$v_displayMessage($v.payload.role_id)"
                :options="roles"
                :disable="disableRole"
                emit-value
                map-options
                stack-label
                label="Role *"
                outlined
              >
                <template v-slot:option="scope">
                  <q-item v-bind="scope.itemProps" v-on="scope.itemEvents">
                    <q-item-section>
                      <q-item-label>{{ scope.opt.label }}</q-item-label>
                      <q-item-label caption>{{ scope.opt.sublabel }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
          </div>
        </q-card-section>
      </template>
    </LmDialog>
  </div>
</template>

<script>
import * as apiTo from '@api/api-routes'
import _cloneDeep from 'lodash/cloneDeep'
import { validations } from './validations'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'

export default {
  components: {
    LmDialog
  },

  mixins: [validations],

  props: {
    modalType: {
      type: String,
      default: 'create'
    },
    modalTitle: {
      type: String,
      default: 'Create User'
    },
    isLastActiveAdmin: Boolean
  },

  data: () => ({
    open: false,
    loading: false,
    createData: null,
    payload: {
      name: '',
      email: '',
      phone: '',
      job: '',
      password: '',
      password_confirmation: '',
      role_id: null,
      active: true
    },
    userToEdit: null
  }),

  computed: {
    roles() {
      if (!this.createData) return []
      let roles = _cloneDeep(this.createData.roles)
      const sublabel = {
        admin: 'All User privileges, plus the ability to manage other users.',
        user: 'Users can view, manage data, process tasks.'
      }
      return roles.map(role => {
        return {
          label: this.$options.filters.capitalize(role.name),
          value: role.id,
          sublabel: sublabel[role.name]
        }
      })
    },
    loggedUser() {
      return this.$store.state.user
    },
    disableRole() {
      if (!this.userToEdit) return
      return (
        (this.isLastActiveAdmin && this.userToEdit.role.name === 'admin') ||
        !this.loggedUser.permissions.some(p => p.name === 'edit-users')
      )
    }
  },

  methods: {
    openModal(payload) {
      this.open = true
      !this.createData && this.getCreateData()
      if (this.modalType === 'edit') {
        this.payload = this.getEditPayload(payload)
        this.userToEdit = payload
      }
    },
    getEditPayload(payload) {
      return {
        id: payload.id,
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        job: payload.job,
        role_id: payload.role ? payload.role.id : payload.role,
        active: payload.active
      }
    },
    getCreateData() {
      this.loading = true
      apiTo
        .getCreateUserData()
        .then(({ data }) => (this.createData = data.result))
        .finally(() => (this.loading = false))
    },
    handleSave() {
      if (!this.$v_validationPass()) return
      if (this.modalType === 'edit') {
        this.updateUser()
      } else {
        this.createUser()
      }
    },
    createUser() {
      this.loading = true
      apiTo
        .createUser(this.payload)
        .then(({ data }) => {
          this.$emit('user-added', data.result)
          this.clearModalData()
        })
        .finally(() => (this.loading = false))
    },
    updateUser() {
      this.loading = true
      const id = this.payload.id
      delete this.payload.id
      apiTo
        .updateUser(this.payload, id)
        .then(({ data }) => {
          this.$emit('user-updated', data.result)
          this.open = false
        })
        .finally(() => (this.loading = false))
    },
    clearModalData() {
      this.$v.$reset()
      const createData = this.createData
      Object.assign(this.$data, this.$options.data.apply(this))
      this.createData = createData
    }
  }
}
</script>
